import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRightLeft } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TransactionPagination } from '@/components/general-ledger/TransactionPagination';
import { FundTransactionStatusBadge } from '@/components/fund-detail/FundTransactionStatusBadge';
import { useFundTransactions } from '@/hooks/react-query/useFundTransactions';
import { type FundTransactionSummary } from '@/types/fund-transactions';
import { formatCurrency, formatDate } from '@/utils/formatters';

/** Short enough to sit under the accounts table without burying the form. */
const PAGE_SIZE = 25;

interface CurrencyTransactionsListProps {
  fundId: string;
}

interface ConversionsPlaceholderProps {
  isLoading: boolean;
  isError: boolean;
}

function ConversionsPlaceholder({
  isLoading,
  isError,
}: ConversionsPlaceholderProps) {
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Loading conversions…</p>
    );
  }
  // Ahead of the empty case, which a failed fetch reads as otherwise:
  // "none recorded" would hide a draft that is waiting for approval.
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        The conversions could not be loaded. Reload before taking this fund as
        having none.
      </p>
    );
  }
  return (
    <div className="text-center py-8 text-muted-foreground">
      <ArrowRightLeft className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
      <p>No conversions recorded yet</p>
      <p className="text-sm">
        Record one above and it is listed here as a draft.
      </p>
    </div>
  );
}

/**
 * Every currency conversion this fund has recorded.
 *
 * The accounts above list posted ledger lines only, so a conversion still in
 * draft appears nowhere on them -- including the one just recorded on this
 * screen. This list is where such a conversion stays visible, and where its
 * status says what it is still waiting for. Period-end retranslations are
 * left out: they restate a balance rather than move money, and they belong
 * to the period-end screen that produced them.
 */
export function CurrencyTransactionsList({
  fundId,
}: CurrencyTransactionsListProps) {
  const navigate = useNavigate();
  // Local rather than in the query string: the section already spends that
  // string on the all-transactions table's own page and filters.
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useFundTransactions(fundId, {
    transaction_type: 'CURRENCY_CONVERSION',
    sort_order: 'desc',
    page,
    page_size: PAGE_SIZE,
  });

  const rows: FundTransactionSummary[] = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversions</CardTitle>
        <CardDescription>
          Everything that moved money between these accounts. A draft is listed
          here before it reaches the ledger.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading || isError || rows.length === 0 ? (
          <ConversionsPlaceholder isLoading={isLoading} isError={isError} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate(
                        `/app/funds/${fundId}/fund-transactions/${row.id}`
                      )
                    }
                    data-testid={`currency-transaction-row-${row.id}`}
                  >
                    <TableCell className="text-muted-foreground">
                      {formatDate(row.transaction_date)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {row.description ?? '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(row.amount, { currency: row.currency })}
                    </TableCell>
                    <TableCell>
                      <FundTransactionStatusBadge status={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {/* Outside the empty branch, so a stale page past the end still offers
            a way back rather than stranding the reader on nothing. */}
        {!isLoading && !isError && totalCount > PAGE_SIZE && (
          <div className="mt-4">
            <TransactionPagination
              page={page}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={setPage}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
